/**
 * دسترسی به برچسب‌های فارسی که `/api/meta` می‌دهد.
 *
 * ⚠️ هیچ برچسبی اینجا نوشته نمی‌شود. متن فارسی هر enum در backend کنار خودِ
 * enum تعریف شده و از `/api/meta` می‌آید. اگر اینجا کپی می‌شد، روزی که یک
 * برچسب عوض شود، این نسخه دروغ می‌گفت.
 *
 * استثنا: `VERDICT_TONE` نگاشت حکم به **لحن بصری** است، نه به متن. آن یک
 * تصمیم طراحی سمت کلاینت است و در backend جایی ندارد.
 */

import type {
  ConditionalDirection,
  LabelCatalogue,
  LabelGroup,
  Verdict,
} from './types'

/** لحن بصری هر حکم. رنگ نشانه است و معنا را متن حمل می‌کند. */
export const VERDICT_TONE: Record<Verdict, 'support' | 'oppose' | 'conditional' | 'neutral'> = {
  support: 'support',
  oppose: 'oppose',
  conditional: 'conditional',
  insufficient_information: 'neutral',
}

/**
 * برچسب سرتیتر حکم، با جهت شرط.
 *
 * ⚠️ **چرا این تابع لازم است:** «مشروط» خالی نمی‌گوید کار انجام شود یا نشود.
 * پاسخی که در متن می‌گفت «پروژه متوقف نشود، اما قرارداد کامل تا احراز شرط‌ها
 * امضا نشود» سرتیترش «مخالف» می‌خورد و خواننده برداشت می‌کرد پروژه رد شد.
 *
 * نگاشت در یک تابع است تا نوار حکم، پیام گفتگو و ردیف آرشیو سه برچسب متفاوت
 * برای یک پاسخ نشان ندهند.
 */
export const verdictHeadline = (
  labels: LabelCatalogue,
  verdict: Verdict,
  direction: ConditionalDirection = '',
): string =>
  verdict === 'conditional' && direction !== ''
    ? labelOf(labels, 'conditional_direction', direction)
    : labelOf(labels, 'verdict', verdict)

/**
 * برچسب یک مقدار enum.
 *
 * در نبود برچسب، مقدار خام برمی‌گردد نه رشته‌ی خالی: نمایش `active` بد است،
 * اما یک سلول خالی که کاربر نمی‌فهمد چرا خالی است، بدتر.
 */
export const labelOf = (
  labels: LabelCatalogue,
  group: LabelGroup,
  value: string | null | undefined,
): string => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  return labels[group]?.[value] ?? value
}

/** گزینه‌های یک select، از کاتالوگ برچسب‌ها. */
export const optionsOf = (
  labels: LabelCatalogue,
  group: LabelGroup,
): { value: string; label: string }[] =>
  Object.entries(labels[group] ?? {}).map(([value, label]) => ({ value, label }))
